import { TaskPlannerSchemaTypes } from "@bfc-software/api-schemas";

type Schemas = TaskPlannerSchemaTypes.components["schemas"];
type TaskPlanDetailResponse = Schemas["TaskPlanDetailResponse"];
type TaskSummary = Schemas["TaskSummary"];
type TaskDetailPick = Schemas["TaskDetailPick"];
type TaskDetailRequirementRecord = Schemas["TaskDetailRequirementRecord"];

export type PalletKey = string;

export type TrailerCellStack = {
  stackPosition: number;
  pick: SimPick;
};

export type TrailerCell = {
  row: number;
  col: number;
  stacks: TrailerCellStack[];
};

export type TruckOverflowBin = {
  palletKey: PalletKey;
  containerId: string;
};

export type Truck = {
  routeId: string;
  routeCode: string;
  routeDate: string;
  cells: TrailerCell[];
  overflow: TruckOverflowBin[];
  bounds: { rows: number; cols: number };
};

export type SimPick = {
  id: string;
  taskId: string;
  detailOrder: number;
  routeId: string;
  palletKey: PalletKey;
  slot: string;
  itemId: string;
  itemName?: string;
  quantity: number;
  uom: string;
  loadLocationKind: "POSITIONED" | "OVERFLOW";
  row?: number;
  col?: number;
  stackPosition: number;
  pickSequence: number;
  /**
   * Barcode values the selector must scan to prove this pick. Only present once
   * the plan has been activated, since requirements are synthesized then.
   */
  slotUpc?: string;
  itemUpc?: string;
};

export type SimTask = {
  id: string;
  taskId: number;
  sortOrder: number;
  subType: "STANDARD" | "BULK";
  picks: SimPick[];
  pallets: PalletKey[];
  slotsWalkOrder: string[];
};

export type WarehouseSlot = {
  slot: string;
  pickSequence: number;
};

// The model exposes slots as a plan-wide ordered list (by pick_sequence);
// the Warehouse component maps that list into a fixed (rows × cols) grid
// derived from the slot count alone, then uniformly scales it to fit the
// available pane. Slot positions are stable across pane resizes and slider
// changes.
export type WarehouseLayout = {
  slots: WarehouseSlot[];
};

export type SimModel = {
  planId: string;
  trucks: Truck[];
  tasks: SimTask[];
  warehouseGrid: WarehouseLayout;
};

export type BuildSimModelInput = {
  plan: TaskPlanDetailResponse;
  tasks: TaskSummary[];
  picks: TaskDetailPick[];
  /**
   * Optional: absent for a plan that has never been activated, in which case
   * picks carry no UPCs.
   */
  requirements?: TaskDetailRequirementRecord[];
};

type PickUpcs = { slotUpc?: string; itemUpc?: string };

function palletKeyFor(pick: TaskDetailPick): PalletKey {
  return pick.lpn ?? `${pick.route_id}|${pick.load_location.container_id}`;
}

/**
 * Collapses the requirement list into the scan codes per pick line. A pick can
 * carry several requirements (slot scan, item scan, count confirmation); only
 * the two that name a barcode are of interest here.
 */
function upcsByPick(
  records: TaskDetailRequirementRecord[],
): Map<string, PickUpcs> {
  const byPick = new Map<string, PickUpcs>();

  for (const record of records) {
    const existing = byPick.get(record.task_detail_pick_id) ?? {};
    switch (record.requirement.kind) {
      case "scan_slot_code":
        existing.slotUpc = record.requirement.slot_upc;
        break;
      case "scan_item_code": {
        const [firstUpc] = record.requirement.item_upcs;
        existing.itemUpc = firstUpc;
        break;
      }
      default:
        continue;
    }
    byPick.set(record.task_detail_pick_id, existing);
  }

  return byPick;
}

function toSimPick(p: TaskDetailPick, upcs?: PickUpcs): SimPick {
  const loc = p.load_location;
  return {
    id: p.id,
    taskId: p.task_id,
    detailOrder: p.detail_order,
    routeId: p.route_id,
    palletKey: palletKeyFor(p),
    slot: p.location,
    itemId: p.item_id,
    itemName: p.item_name,
    quantity: p.quantity,
    uom: p.uom,
    loadLocationKind: loc.type,
    row: loc.row,
    col: loc.column,
    stackPosition: loc.stack_position ?? 0,
    pickSequence: p.pick_sequence,
    slotUpc: upcs?.slotUpc,
    itemUpc: upcs?.itemUpc,
  };
}

function buildTrucks(
  plan: TaskPlanDetailResponse,
  rawPicks: TaskDetailPick[],
  simPicksById: Map<string, SimPick>,
): Truck[] {
  return plan.routes.map((route) => {
    const routePicks = rawPicks.filter((p) => p.route_id === route.id);

    const cellByKey = new Map<string, TrailerCell>();
    const seenStackKey = new Set<string>();
    const seenOverflow = new Set<PalletKey>();
    const overflow: TruckOverflowBin[] = [];

    let maxRow = 0;
    let maxCol = 0;

    for (const raw of routePicks) {
      const loc = raw.load_location;
      const sim = simPicksById.get(raw.id);
      if (!sim) continue;
      if (loc.type === "POSITIONED" && loc.row != null && loc.column != null) {
        const cellKey = `${loc.row}|${loc.column}`;
        let cell = cellByKey.get(cellKey);
        if (!cell) {
          cell = { row: loc.row, col: loc.column, stacks: [] };
          cellByKey.set(cellKey, cell);
        }
        const stackKey = `${cellKey}|${sim.stackPosition}`;
        if (!seenStackKey.has(stackKey)) {
          seenStackKey.add(stackKey);
          cell.stacks.push({ stackPosition: sim.stackPosition, pick: sim });
        }
        if (loc.row > maxRow) maxRow = loc.row;
        if (loc.column > maxCol) maxCol = loc.column;
      } else if (loc.type === "OVERFLOW") {
        if (!seenOverflow.has(sim.palletKey)) {
          seenOverflow.add(sim.palletKey);
          overflow.push({
            palletKey: sim.palletKey,
            containerId: loc.container_id,
          });
        }
      }
    }

    const cells: TrailerCell[] = Array.from(cellByKey.values()).map((cell) => ({
      ...cell,
      stacks: cell.stacks
        .slice()
        .sort((a, b) => a.stackPosition - b.stackPosition),
    }));

    return {
      routeId: route.id,
      routeCode: route.route_code,
      routeDate: route.route_date,
      cells,
      overflow,
      bounds: { rows: Math.max(maxRow, 1), cols: Math.max(maxCol, 1) },
    };
  });
}

function buildTask(task: TaskSummary, allSimPicks: SimPick[]): SimTask {
  const simPicks = allSimPicks
    .filter((p) => p.taskId === task.id)
    .slice()
    .sort((a, b) => a.detailOrder - b.detailOrder);

  const seenPallets = new Set<PalletKey>();
  const pallets: PalletKey[] = [];
  for (const pick of simPicks) {
    if (!seenPallets.has(pick.palletKey)) {
      seenPallets.add(pick.palletKey);
      pallets.push(pick.palletKey);
    }
  }

  const seenSlots = new Set<string>();
  const slotsWalkOrder: string[] = [];
  const slotOrdered = simPicks
    .slice()
    .sort((a, b) => a.pickSequence - b.pickSequence);
  for (const pick of slotOrdered) {
    if (!seenSlots.has(pick.slot)) {
      seenSlots.add(pick.slot);
      slotsWalkOrder.push(pick.slot);
    }
  }

  return {
    id: task.id,
    taskId: task.task_id,
    sortOrder: task.sort_order,
    subType: task.sub_type,
    picks: simPicks,
    pallets,
    slotsWalkOrder,
  };
}

function buildWarehouseLayout(picks: TaskDetailPick[]): WarehouseLayout {
  const slotToSeq = new Map<string, number>();
  for (const p of picks) {
    if (p.load_location.type !== "POSITIONED") continue;
    const existing = slotToSeq.get(p.location);
    if (existing == null || p.pick_sequence < existing) {
      slotToSeq.set(p.location, p.pick_sequence);
    }
  }

  const ordered = Array.from(slotToSeq.entries()).sort(
    (a, b) => a[1] - b[1] || a[0].localeCompare(b[0]),
  );

  const seenSeq = new Set<number>();
  const slots: WarehouseSlot[] = [];
  for (const [slot, ps] of ordered) {
    // Two distinct slots that share a pick_sequence are treated as the same
    // physical location; keep the first encountered slot.
    if (seenSeq.has(ps)) continue;
    seenSeq.add(ps);
    slots.push({ slot, pickSequence: ps });
  }

  return { slots };
}

export function buildSimModel({
  plan,
  tasks,
  picks,
  requirements,
}: BuildSimModelInput): SimModel {
  const upcs = upcsByPick(requirements ?? []);
  const simPicks = picks.map((pick) => toSimPick(pick, upcs.get(pick.id)));
  const simPicksById = new Map(simPicks.map((p) => [p.id, p]));

  const orderedTasks = tasks
    .slice()
    .sort((a, b) => a.sort_order - b.sort_order)
    .map((task) => buildTask(task, simPicks));

  return {
    planId: plan.id,
    trucks: buildTrucks(plan, picks, simPicksById),
    tasks: orderedTasks,
    warehouseGrid: buildWarehouseLayout(picks),
  };
}
